import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workspaceApi } from '../services/api';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import './WorkspaceList.css';

const STATUS_LABELS = {
  pending: 'Scheduled',
  posting: 'Posting…',
  posted: 'Posted',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const ArticleScheduled = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user, isAuthenticated } = useAuth();
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [newDatetime, setNewDatetime] = useState('');

  const author = user?.username || localStorage.getItem('hive_username');

  const load = useCallback(async () => {
    if (!author) return;
    setLoading(true);
    setError('');
    try {
      const data = await workspaceApi.listScheduled(author);
      setScheduled(data.scheduled || []);
    } catch {
      setError('Failed to load scheduled posts.');
    } finally {
      setLoading(false);
    }
  }, [author]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled post?')) return;
    setActionLoading(id + '-cancel');
    try {
      await workspaceApi.cancelScheduled(id, author);
      setScheduled(prev => prev.map(s => s.id === id ? { ...s, status: 'cancelled' } : s));
    } catch {
      alert('Failed to cancel scheduled post.');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePostNow = async (id) => {
    if (!window.confirm('Post this article immediately?')) return;
    setActionLoading(id + '-post-now');
    try {
      await workspaceApi.postNow(id, author);
      setScheduled(prev => prev.map(s => s.id === id ? { ...s, status: 'posting' } : s));
    } catch (err) {
      alert('Failed to trigger post: ' + (err?.message || 'Unknown error'));
    } finally {
      setActionLoading(null);
    }
  };

  const openReschedule = (item) => {
    const local = new Date(item.scheduled_at).toISOString().slice(0, 16);
    setNewDatetime(local);
    setRescheduleTarget(item.id);
  };

  const handleReschedule = async () => {
    if (!newDatetime) return;
    const dt = new Date(newDatetime);
    if (dt <= new Date()) { alert('Please choose a future date and time.'); return; }
    setActionLoading(rescheduleTarget + '-reschedule');
    try {
      await workspaceApi.reschedule(rescheduleTarget, { author, scheduled_at: dt.toISOString() });
      setScheduled(prev => prev.map(s => s.id === rescheduleTarget ? { ...s, scheduled_at: dt.toISOString(), status: 'pending' } : s));
      setRescheduleTarget(null);
    } catch {
      alert('Failed to reschedule.');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAuthenticated) {
    return <div className="workspace-list-page"><p>Please log in.</p></div>;
  }

  return (
    <>
      <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="workspace-list-page">
        <div className="workspace-list-header">
          <h2>Scheduled Posts</h2>
          <Link to="/create-article" className="workspace-new-btn">+ New Article</Link>
        </div>

        {loading && <LoadingSpinner text="Loading scheduled posts..." />}
        {error && <div className="workspace-error">{error}<button onClick={load}>Retry</button></div>}

        {!loading && !error && scheduled.length === 0 && (
          <div className="workspace-empty">
            <p>No scheduled posts.</p>
            <Link to="/create-article" className="workspace-new-btn">Schedule one</Link>
          </div>
        )}

        {rescheduleTarget && (
          <div className="workspace-modal-overlay">
            <div className="workspace-modal">
              <h3>Reschedule Post</h3>
              <input
                type="datetime-local"
                value={newDatetime}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                onChange={e => setNewDatetime(e.target.value)}
                className="workspace-datetime-input"
              />
              <div className="workspace-modal-actions">
                <button onClick={handleReschedule} disabled={actionLoading === rescheduleTarget + '-reschedule'} className="workspace-action-btn">
                  {actionLoading === rescheduleTarget + '-reschedule' ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setRescheduleTarget(null)} className="workspace-action-btn">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {!loading && scheduled.length > 0 && (
          <div className="workspace-item-list">
            {scheduled.map(item => (
              <div key={item.id} className={`workspace-item workspace-item--${item.status}`}>
                <div className="workspace-item-info">
                  <div className="workspace-item-title">{item.title || <em>Untitled</em>}</div>
                  <div className="workspace-item-meta">
                    <span className={`workspace-status-badge workspace-status--${item.status}`}>
                      {STATUS_LABELS[item.status] || item.status}
                    </span>
                    <span>{new Date(item.scheduled_at).toLocaleString()}</span>
                    {item.community && <span>{item.community}</span>}
                    {item.error_message && <span className="workspace-error-msg" title={item.error_message}>⚠ Error</span>}
                  </div>
                </div>
                {(item.status === 'pending' || item.status === 'failed') && (
                  <div className="workspace-item-actions">
                    <button className="workspace-action-btn" onClick={() => openReschedule(item)} disabled={!!actionLoading}>
                      Reschedule
                    </button>
                    <button className="workspace-action-btn" onClick={() => handlePostNow(item.id)} disabled={!!actionLoading}>
                      {item.status === 'failed' ? 'Retry Now' : 'Post Now'}
                    </button>
                    <button className="workspace-action-btn workspace-delete-btn" onClick={() => handleCancel(item.id)} disabled={!!actionLoading}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ArticleScheduled;
